/**
 * Modal form for creating a ContentItem on a Site (issue #173).
 *
 * Posts to `POST /api/admin/sites/:siteId/content-items` via
 * `useCreateContentItem`. Mirrors `CreatePageDialog` but takes the
 * type/blockType pair the API expects:
 *
 *   - `name`       — operator-supplied display name.
 *   - `type`       — required. PAGE_BLOCK / BLOG_POST / PRODUCT / etc.
 *   - `blockType`  — required iff `type === 'PAGE_BLOCK'`; forbidden
 *                    otherwise. The shared `createContentItemSchema`
 *                    enforces both halves of the pairing with a
 *                    superRefine; we surface the required/forbidden
 *                    flip in the UI as a conditional render so the
 *                    Block-type field only appears for PAGE_BLOCK
 *                    items. The Zod resolver catches any mismatch the
 *                    operator manages to produce by switching back and
 *                    forth.
 *
 * The shared `createContentItemSchema` is the single source of validation;
 * the resolver wraps it for react-hook-form per `react-patterns.md`.
 */

import { useEffect } from "react";
import type { ReactElement } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { useForm, useWatch } from "react-hook-form";
import {
  type BlockType,
  type ContentItemType,
  type CreateContentItemInput,
  createContentItemSchema,
} from "@ottopilot/shared";
import { useCreateContentItem } from "../../hooks/useContentItems";
import { useToast } from "../../components/ToastProvider";
import { makeZodResolver } from "../../lib/zodResolver";

const CONTENT_ITEM_TYPES: ContentItemType[] = [
  "PAGE_BLOCK",
  "BLOG_POST",
  "PRODUCT",
  "FAQ",
  "CTA",
  "IMAGE",
  "SEO",
  "CUSTOM",
];

const BLOCK_TYPES: BlockType[] = [
  "HERO",
  "TEXT",
  "FEATURE_GRID",
  "BENEFITS",
  "PROBLEM_SOLUTION",
  "CTA",
  "FAQ",
  "TESTIMONIALS",
  "PRODUCT_GRID",
  "BLOG_CONTENT",
  "BLOG_LIST",
  "CONTACT_FORM",
  "IMAGE",
  "COMPARISON_TABLE",
  "PRICING_TABLE",
  "TRUST_BADGES",
  "NEWSLETTER",
];

type Props = {
  open: boolean;
  onClose: () => void;
  siteId: string;
};

type FormValues = {
  name: string;
  type: ContentItemType;
  blockType?: BlockType;
};

export function CreateContentItemDialog({ open, onClose, siteId }: Props): ReactElement {
  const create = useCreateContentItem(siteId);
  const toast = useToast();

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: makeZodResolver(createContentItemSchema),
    defaultValues: { name: "", type: "PAGE_BLOCK", blockType: "HERO" },
  });

  // Watch the type field so the blockType TextField only renders when
  // `type === 'PAGE_BLOCK'`. The shared Zod schema's superRefine enforces
  // the same rule on the wire; the conditional render keeps the form
  // ergonomic by hiding a field that can't be valid for non-PAGE_BLOCK
  // items.
  const type = useWatch({ control, name: "type" });

  useEffect(() => {
    if (open) reset({ name: "", type: "PAGE_BLOCK", blockType: "HERO" });
  }, [open, reset]);

  const onSubmit = handleSubmit((values) => {
    // Strip blockType when the type isn't PAGE_BLOCK — the strict() Zod
    // schema would reject the extra field if we forwarded the dropdown's
    // default unchanged on a non-PAGE_BLOCK submission.
    const input: CreateContentItemInput =
      values.type === "PAGE_BLOCK"
        ? {
            name: values.name,
            type: values.type,
            ...(values.blockType !== undefined ? { blockType: values.blockType } : {}),
          }
        : { name: values.name, type: values.type };
    create.mutate(input, {
      onSuccess: (result) => {
        toast.showSuccess(`Created content item ${result.name}.`);
        onClose();
      },
      onError: (err) => {
        toast.showError(err.message || "Could not create content item.");
      },
    });
  });

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!create.isPending) onClose();
      }}
      maxWidth="sm"
      fullWidth
      data-testid="create-content-item-dialog"
    >
      <DialogTitle>New content item</DialogTitle>
      <Stack
        component="form"
        onSubmit={(e) => {
          void onSubmit(e);
        }}
        noValidate
      >
        <DialogContent>
          <Stack spacing={2}>
            {create.isError ? <Alert severity="error">{create.error.message}</Alert> : null}
            <TextField
              label="Name"
              fullWidth
              autoFocus
              error={Boolean(errors.name)}
              helperText={errors.name?.message ?? "Operator-facing display name."}
              inputProps={{ "data-testid": "create-content-item-name-input" }}
              {...register("name")}
            />
            <TextField
              select
              label="Type"
              fullWidth
              defaultValue="PAGE_BLOCK"
              error={Boolean(errors.type)}
              helperText={errors.type?.message}
              SelectProps={{
                SelectDisplayProps: { "data-testid": "create-content-item-type-select" } as Record<
                  string,
                  string
                >,
              }}
              {...register("type")}
            >
              {CONTENT_ITEM_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
            {type === "PAGE_BLOCK" ? (
              <TextField
                select
                label="Block type"
                fullWidth
                defaultValue="HERO"
                error={Boolean(errors.blockType)}
                helperText={
                  errors.blockType?.message ??
                  "Required for PAGE_BLOCK items. Drives the renderer template."
                }
                SelectProps={{
                  SelectDisplayProps: {
                    "data-testid": "create-content-item-block-type-select",
                  } as Record<string, string>,
                }}
                {...register("blockType")}
              >
                {BLOCK_TYPES.map((bt) => (
                  <MenuItem key={bt} value={bt}>
                    {bt}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            disabled={create.isPending}
            data-testid="create-content-item-cancel-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || create.isPending}
            data-testid="create-content-item-submit-button"
          >
            {create.isPending ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Stack>
    </Dialog>
  );
}
